import { InfoCard } from '#app/components/ui/info-card.tsx'
import { type Route } from './+types/sample-schedule.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Sample Schedule | DanceMakers' },
	{
		name: 'description',
		content:
			'View a sample weekend schedule for DanceMakers convention and competition including Friday competition, Saturday and Sunday classes, and the Wrap It Ceremony.',
	},
]

export default function SampleScheduleRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						2026-27 Season
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Sample Schedule
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						A typical DanceMakers convention and competition weekend. Times may
						vary by city – check your specific event page for exact times.
					</p>
				</div>
			</section>

			{/* Friday */}
			<section className="bg-muted/30 py-8 md:py-12">
				<div className="container px-6 md:px-16">
					<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
						Friday
					</h2>

					<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
						<InfoCard title="Registration">
							<p>
								Time varies depending on competition start time.{' '}
								<span className="font-medium text-foreground">
									Registration will always open at least one hour before
									competition begins.
								</span>
							</p>
						</InfoCard>

						<InfoCard title="Competition">
							<p className="mb-3">
								Friday competition start times may vary. Friday competition
								typically starts at{' '}
								<span className="font-medium text-foreground">8 AM</span> and
								begins with solos.
							</p>
							<p className="mb-3">
								After completing the solo competition it is possible, but not
								guaranteed we will move into other routines such as duo/trio and
								groups.
							</p>
							<p className="text-xs italic">
								If any routine that is scheduled on Friday is unable to perform
								due to school, DanceMakers is happy to provide the studio will a
								full refund for those routines.
							</p>
						</InfoCard>

						<InfoCard title="Awards">
							<p className="mb-3">
								<span className="font-medium text-foreground">All Solos</span>{' '}
								will receive a Solo Participation Award directly following their
								routine.
							</p>
							<p className="mb-3">
								<span className="font-medium text-foreground">
									All Duo/Trio and Group routines
								</span>{' '}
								will be presented an Adjudication Award plus all Style High
								Scores will be given during their designated Awards Ceremony.
							</p>
							<p>
								Times will vary depending on competition.{' '}
								<span className="font-medium text-foreground">
									All Overall Awards will be presented Sunday at the Wrap It
									Ceremony.
								</span>
							</p>
						</InfoCard>

						<InfoCard title="Friday Night Bonus Class">
							<p>
								DanceMakers may offer bonus classes on Friday evenings! Times
								could range from{' '}
								<span className="font-medium text-foreground">5 - 10 PM</span>.
							</p>
						</InfoCard>
					</div>
				</div>
			</section>

			{/* Saturday */}
			<section className="py-10 md:py-16">
				<div className="container px-6 md:px-16">
					<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
						Saturday
					</h2>

					<div className="-mx-6 overflow-x-auto px-6 md:mx-0 md:px-0">
						<table className="w-full min-w-[600px] border border-muted-foreground/30 text-xs sm:text-sm">
							<thead>
								<tr className="bg-muted/30">
									<th className="w-[120px] border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Time
									</th>
									<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Senior
									</th>
									<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Teen
									</th>
									<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Junior
									</th>
									<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Teacher / Camp
									</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										7 AM
									</td>
									<td
										colSpan={4}
										className="border-b border-muted-foreground/30 p-2 text-center font-medium text-foreground sm:p-3"
									>
										Registration
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										7:30 AM
									</td>
									<td
										colSpan={4}
										className="border-b border-muted-foreground/30 p-2 text-center font-medium text-primary sm:p-3"
									>
										Intros & Warm Up
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										8 - 9 AM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Teacher
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										9 - 10 AM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Teacher
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										10 - 10:30 AM
									</td>
									<td
										colSpan={4}
										className="border-b border-muted-foreground/30 p-2 text-center italic text-muted-foreground sm:p-3"
									>
										Break
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										10:30 - 11:15 AM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 italic text-muted-foreground sm:p-3">
										Lunch
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Camp Dance
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										11:15 AM - 12 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 italic text-muted-foreground sm:p-3">
										Lunch
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Camp Dance
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										12 - 12:45 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 italic text-muted-foreground sm:p-3">
										Lunch
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 italic text-muted-foreground sm:p-3">
										Lunch
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										12:45 - 1:30 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Camp Dance
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										1:30 - 2:30 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Teacher
									</td>
								</tr>
								<tr className="bg-accent/10">
									<td className="whitespace-nowrap border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										3:30 PM - Until
									</td>
									<td colSpan={4} className="p-2 sm:p-3">
										<span className="font-medium text-foreground">
											Competition
										</span>
										<span className="text-muted-foreground">
											{' '}
											– Start Time May Vary
										</span>
										<p className="mt-2 text-xs text-muted-foreground">
											<span className="font-medium text-foreground">
												Awards
											</span>{' '}
											– All Duo/Trio, Group, Line, Extended Line, and Production
											routines will be presented an Adjudication Award plus all
											Style High Scores will be given during their designated
											Awards Ceremony.
										</p>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</section>

			{/* Sunday */}
			<section className="bg-muted/30 py-10 md:py-16">
				<div className="container px-6 md:px-16">
					<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
						Sunday
					</h2>

					<div className="-mx-6 overflow-x-auto px-6 md:mx-0 md:px-0">
						<table className="w-full min-w-[600px] border border-muted-foreground/30 text-xs sm:text-sm">
							<thead>
								<tr className="bg-muted/30">
									<th className="w-[120px] border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Time
									</th>
									<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Senior
									</th>
									<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Teen
									</th>
									<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Junior
									</th>
									<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-3">
										Teacher / Camp
									</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										7 - 8 AM
									</td>
									<td
										colSpan={4}
										className="border-b border-muted-foreground/30 p-2 font-medium text-primary sm:p-3"
									>
										Advanced Ballet Class for Teens and Seniors
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										7:30 - 11 AM
									</td>
									<td
										colSpan={4}
										className="border-b border-muted-foreground/30 p-2 text-center font-medium text-foreground sm:p-3"
									>
										Registration
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										8 - 9 AM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Teacher
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										9 - 10 AM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Teacher
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										10 - 10:30 AM
									</td>
									<td
										colSpan={4}
										className="border-b border-muted-foreground/30 p-2 text-center italic text-muted-foreground sm:p-3"
									>
										Break
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										10:30 - 11:15 AM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 italic text-muted-foreground sm:p-3">
										Lunch
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Camp Dance
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										11:15 AM - 12 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 italic text-muted-foreground sm:p-3">
										Lunch
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Camp Dance
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										12 - 12:45 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 italic text-muted-foreground sm:p-3">
										Lunch
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 font-medium text-primary sm:p-3">
										Teachers Luncheon
									</td>
								</tr>
								<tr>
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										12:45 - 1:30 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3">
										Class - Camp Dance
									</td>
								</tr>
								<tr className="bg-muted/10">
									<td className="whitespace-nowrap border-b border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										1:30 - 2:30 PM
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-3">
										Class
									</td>
									<td className="border-b border-muted-foreground/30 p-2 sm:p-3"></td>
								</tr>
								<tr className="bg-primary/10">
									<td className="whitespace-nowrap border-r border-muted-foreground/30 p-2 font-medium sm:p-3">
										2:30 - 3:45 PM
									</td>
									<td colSpan={4} className="p-2 sm:p-3">
										<span className="text-base font-bold text-primary">
											Wrap It Ceremony
										</span>
										<p className="mt-1 text-xs text-muted-foreground">
											Everyone Welcome! No wristbands required.
										</p>
										<p className="mt-1 text-xs font-medium text-foreground">
											All Overall Awards from Competition plus Scholarships from
											Convention will be awarded!
										</p>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</section>

			{/* Important Notes */}
			<section className="py-10 md:py-16">
				<div className="container px-6 md:px-16">
					<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
						Important Notes
					</h2>

					<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
						<InfoCard title="Schedule May Vary">
							<p>
								This is a{' '}
								<span className="font-medium text-foreground">
									sample schedule
								</span>
								. Actual times may vary by city and event. Please check your
								specific event page for exact times once detailed information is
								released{' '}
								<span className="font-medium text-foreground">
									10 days before your event
								</span>
								.
							</p>
						</InfoCard>

						<InfoCard title="Competition Times">
							<p>
								Competition start times vary based on the number of entries.
								Friday competition typically begins at{' '}
								<span className="font-medium text-foreground">8 AM</span> with
								solos. Saturday competition usually begins around{' '}
								<span className="font-medium text-foreground">3:30 PM</span>{' '}
								after convention classes conclude.
							</p>
						</InfoCard>

						<InfoCard title="Staggered Lunches">
							<p>
								Lunch times are staggered by age division to ensure smooth
								transitions between classes. Please note your dancer's specific
								lunch time based on their age group.
							</p>
						</InfoCard>

						<InfoCard title="Wrap It Ceremony">
							<p>
								The Wrap It Ceremony on Sunday is{' '}
								<span className="font-medium text-foreground">
									open to everyone
								</span>{' '}
								– no wristband required! All overall awards and scholarships are
								presented at this time.
							</p>
						</InfoCard>
					</div>
				</div>
			</section>

			{/* Contact CTA */}
			<section className="bg-muted/30 py-10 md:py-16">
				<div className="container px-6 text-center md:px-16">
					<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
						Questions About the Schedule?
					</h2>
					<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
						Have questions about convention or competition scheduling? Our team
						is here to help!
					</p>
					<a
						href="/contact"
						className="inline-block border border-primary px-8 py-3 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
					>
						Contact Us
					</a>
				</div>
			</section>
		</div>
	)
}
